import type { ReactNode } from "react";

/**
 * 自建图标集（零第三方依赖）——统一 24×24 视图框、线性描边、currentColor 继承。
 *
 * 命名约定：与旧前端的 @ant-design/icons 语义一一对应（去掉 Outlined 后缀转 kebab-case），
 * 便于逐页迁移时「旧图标 → 新图标」机械替换，避免语义漂移。
 * 新增图标请保持同一几何规范：stroke 宽度由 <Icon> 统一给，path 内不写 stroke/fill 颜色。
 */
export const ICON_PATHS = {
  /* ── 导航 / 业务分组 ── */
  dashboard: (
    <>
      <rect x="3" y="4" width="18" height="12.5" rx="2.5" />
      <path d="M8 20.5h8M12 16.5v4" />
    </>
  ),
  appstore: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="2" />
      <rect x="13" y="4" width="7" height="7" rx="2" />
      <rect x="4" y="13" width="7" height="7" rx="2" />
      <rect x="13" y="13" width="7" height="7" rx="2" />
    </>
  ),
  inbox: (
    <>
      <path d="M5.5 5h13l2.5 8v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5z" />
      <path d="M3 13h5l1.4 2.4h5.2L16 13h5" />
    </>
  ),
  "shopping-cart": (
    <>
      <circle cx="9.5" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M3 4h2.2l2.3 10.3A1.6 1.6 0 0 0 9.1 15.5h9.4a1.6 1.6 0 0 0 1.6-1.3L21 7H6" />
    </>
  ),
  schedule: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M8 3v4M16 3v4M3 10.5h18" />
      <path d="M8.5 14.5h3M8.5 17.5h7M14.5 14.5h1" />
    </>
  ),
  tool: (
    <>
      <path d="M9.6 3.4a5.5 5.5 0 0 0 6.8 7l4.1 4.1a2 2 0 0 1-2.8 2.8l-4.1-4.1a5.5 5.5 0 0 0-7-6.8l3 3 1.5-1.5z" />
    </>
  ),
  dollar: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.6 7.4 12 12l3.4-4.6M9.4 12.4h5.2M9.4 15h5.2M12 12v5.4" />
    </>
  ),
  safety: (
    <>
      <path d="M12 3 5 6v5.6c0 4.2 2.8 7.3 7 9.4 4.2-2.1 7-5.2 7-9.4V6z" />
      <path d="M9 12.1l2.2 2.2L15.4 10" />
    </>
  ),
  "node-index": (
    <>
      <rect x="3" y="3" width="6.5" height="6.5" rx="1.8" />
      <rect x="14.5" y="14.5" width="6.5" height="6.5" rx="1.8" />
      <path d="M9.5 6.2h4.3a3.4 3.4 0 0 1 3.4 3.4v4.9" />
    </>
  ),
  audit: (
    <>
      <rect x="5" y="4.5" width="14" height="16.5" rx="2.5" />
      <path d="M9.5 4.5V3.6A1.6 1.6 0 0 1 11.1 2h1.8a1.6 1.6 0 0 1 1.6 1.6v.9" />
      <path d="M9.2 13.6l2 2 3.8-4.2" />
    </>
  ),
  bell: (
    <>
      <path d="M18 16V11a6 6 0 1 0-12 0v5l-1.6 2.2h15.2z" />
      <path d="M10 21h4" />
    </>
  ),
  team: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.6 20a6.4 6.4 0 0 1 12.8 0" />
      <path d="M16 5.3a3.5 3.5 0 0 1 0 6.5M17.6 20a6.4 6.4 0 0 0-1.8-4.5" />
    </>
  ),
  "check-square": (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3.5" />
      <path d="M8 12.3l2.7 2.7L16.4 9" />
    </>
  ),
  shop: (
    <>
      <path d="M4.5 9.6V20h15V9.6" />
      <path d="M3 9.6 5.6 4h12.8L21 9.6z" />
      <path d="M9.5 20v-5h5v5" />
    </>
  ),
  form: (
    <>
      <rect x="4.5" y="3" width="15" height="18" rx="2.5" />
      <path d="M8.5 8h7M8.5 12h7M8.5 16h4" />
    </>
  ),
  percentage: (
    <>
      <circle cx="8" cy="8" r="2.4" />
      <circle cx="16" cy="16" r="2.4" />
      <path d="M18.2 5.8 5.8 18.2" />
    </>
  ),
  build: (
    <>
      <path d="M14.6 3.6 20 9l-2.2 2.2-2-2L7 18l-3-3 9.6-9.6-2-2z" />
    </>
  ),
  code: <path d="M9.2 8.4 4.6 12l4.6 3.6M14.8 8.4 19.4 12l-4.6 3.6" />,
  "file-text": (
    <>
      <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4.4 21 19.6H3z" />
      <path d="M12 10v4.4M12 17.1v.4" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4.4 21 19.6H3z" />
      <path d="M12 10v4.4" />
      <circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  "deployment-unit": (
    <>
      <path d="M12 3 20 7.5v9L12 21 4 16.5v-9z" />
      <path d="M12 12 20 7.5M12 12v9M12 12 4 7.5" />
    </>
  ),
  partition: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M9.5 4v16M14.5 4v16" />
    </>
  ),
  stop: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M6.4 6.4 17.6 17.6" />
    </>
  ),
  "bar-chart": <path d="M5.5 20V10M12 20V4M18.5 20v-6.6" />,
  message: <path d="M4 5.5h16V17H9.4L4 20.6z" />,
  scan: (
    <>
      <path d="M4 8.5V6.4A2.4 2.4 0 0 1 6.4 4h2.1M15.5 4h2.1A2.4 2.4 0 0 1 20 6.4v2.1M20 15.5v2.1a2.4 2.4 0 0 1-2.4 2.4h-2.1M8.5 20H6.4A2.4 2.4 0 0 1 4 17.6v-2.1" />
      <path d="M4 12h16" />
    </>
  ),
  bulb: (
    <>
      <path d="M12 3a6 6 0 0 0-3.5 10.9V16h7v-2.1A6 6 0 0 0 12 3z" />
      <path d="M9.6 18.6h4.8M10.4 21h3.2" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 4h8v4.6a4 4 0 0 1-8 0z" />
      <path d="M8 5.8H5.6a2.6 2.6 0 0 0 2.6 4.6M16 5.8h2.4A2.6 2.6 0 0 1 16 10.4" />
      <path d="M12 12.6V16M9.6 20h4.8M10.4 16h3.2v4h-3.2z" />
    </>
  ),
  fund: (
    <>
      <path d="M4 18.5 9.6 12.6l3.4 3L20 7.8" />
      <path d="M15.4 7.8H20v4.6" />
    </>
  ),
  setting: (
    <>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M12 3v2.4M12 18.6V21M3 12h2.4M18.6 12H21M5.6 5.6l1.7 1.7M16.7 16.7l1.7 1.7M18.4 5.6l-1.7 1.7M7.3 16.7l-1.7 1.7" />
    </>
  ),
  idcard: (
    <>
      <rect x="2.6" y="5" width="18.8" height="14" rx="2.6" />
      <circle cx="8.6" cy="10.8" r="1.9" />
      <path d="M5.6 15.6a3.4 3.4 0 0 1 6 0M14 10.4h4.6M14 13.6h4.6" />
    </>
  ),

  /* ── 通用 UI ── */
  check: <path d="M5 12.6 9.4 17 19 7.4" />,
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.2l2.8 2.8L16 9.6" />
    </>
  ),
  "close-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.4M12 7.6v.4" />
    </>
  ),
  close: <path d="M6.2 6.2l11.6 11.6M17.8 6.2 6.2 17.8" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.4" />
      <path d="M15.8 15.8 20.5 20.5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  "chevron-down": <path d="M6.5 9.5 12 15l5.5-5.5" />,
  "chevron-right": <path d="M9.5 6.5 15 12l-5.5 5.5" />,
  refresh: (
    <>
      <path d="M20 11.5A8 8 0 0 0 6.3 6.3L4 8.6" />
      <path d="M4 4.6v4h4" />
      <path d="M4 12.5a8 8 0 0 0 13.7 5.2L20 15.4" />
      <path d="M20 19.4v-4h-4" />
    </>
  ),
  download: (
    <>
      <path d="M12 3.5v11M7.8 10.5 12 14.7l4.2-4.2" />
      <path d="M4.5 19.5h15" />
    </>
  ),
  upload: (
    <>
      <path d="M12 20.5v-11M7.8 13.5 12 9.3l4.2 4.2" />
      <path d="M4.5 4.5h15" />
    </>
  ),
  "arrow-up": <path d="M12 19.5V5M6.2 10.8 12 5l5.8 5.8" />,
  "arrow-down": <path d="M12 4.5V19M6.2 13.2 12 19l5.8-5.8" />,
  "arrow-right": <path d="M4.5 12h15M13.7 6.2 19.5 12l-5.8 5.8" />,

  /* ── 页面级图标（覆盖旧前端用到的全部 antd 图标）── */
  apartment: (
    <>
      <path d="M12 3v4M6 21v-5h12v5" />
      <rect x="3" y="7" width="6" height="6" rx="1.5" />
      <rect x="15" y="7" width="6" height="6" rx="1.5" />
    </>
  ),
  bug: (
    <>
      <path d="M8 8.5a4 4 0 0 1 8 0v5a4 4 0 0 1-8 0z" />
      <path d="M12 8.5V4.5M8 12H4M20 12h-4M8 16l-3 3M16 16l3 3M8 10.5 5 7.5M16 10.5l3-3" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8.6h3L8.5 6.5h7l1.5 2.1h3V19H4z" />
      <circle cx="12" cy="13.4" r="3.2" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11.5" height="11.5" rx="2.5" />
      <path d="M15.5 8.5V6.2A2.2 2.2 0 0 0 13.3 4H6.2A2.2 2.2 0 0 0 4 6.2v7.1a2.2 2.2 0 0 0 2.2 2.2h2.3" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="7.5" ry="3" />
      <path d="M4.5 6v12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V6M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
    </>
  ),
  delete: (
    <>
      <path d="M4 6.5h16M9.5 6.5V4h5v2.5" />
      <path d="M6.6 6.5 7.6 20h8.8l1-13.5M10.5 10v6.5M13.5 10v6.5" />
    </>
  ),
  dingding: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.4 12.4h7.2M9.4 15.2h4.6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4L20 8l-4-4L4 16z" />
      <path d="M14.5 5.5 18.5 9.5" />
    </>
  ),
  "exclamation-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.4V13M12 16.4v.4" />
    </>
  ),
  "expand-alt": <path d="M9.5 4.5H4.5v5M14.5 19.5h5v-5M19.5 9.5v-5h-5M4.5 14.5v5h5" />,
  experiment: (
    <>
      <path d="M9.2 3h5.6M10.6 3v6L5.8 17.6A1.7 1.7 0 0 0 7.3 20.2h9.4a1.7 1.7 0 0 0 1.5-2.6L13.4 9V3" />
      <path d="M7.6 14.8h8.8" />
    </>
  ),
  eye: (
    <>
      <path d="M2.6 12S6.2 6.6 12 6.6 21.4 12 21.4 12 17.8 17.4 12 17.4 2.6 12 2.6 12z" />
      <circle cx="12" cy="12" r="2.9" />
    </>
  ),
  "file-excel": (
    <>
      <path d="M14 3H7.6A2.6 2.6 0 0 0 5 5.6v12.8A2.6 2.6 0 0 0 7.6 21h8.8a2.6 2.6 0 0 0 2.6-2.6V8z" />
      <path d="M14 3v5h5M9.4 12.6l5.2 4.8M14.6 12.6l-5.2 4.8" />
    </>
  ),
  fullscreen: <path d="M4.5 9V4.5h4.5M15 4.5h4.5V9M19.5 15v4.5H15M9 19.5H4.5V15" />,
  history: (
    <>
      <path d="M3.8 12a8.2 8.2 0 1 0 2.5-5.9" />
      <path d="M3.8 4.6v4.2h4.2" />
      <path d="M12 8v4.4l3 1.8" />
    </>
  ),
  home: (
    <>
      <path d="M4 10.4 12 4l8 6.4V20H4z" />
      <path d="M9.5 20v-5.4h5V20" />
    </>
  ),
  key: (
    <>
      <circle cx="8.4" cy="14" r="3.9" />
      <path d="M11.3 11.1 20 2.8M16.4 6.4l2.4 2.4M13.9 8.9l2.4 2.4" />
    </>
  ),
  link: (
    <>
      <path d="M9.6 14.4 14.4 9.6" />
      <path d="M11.2 6.8 13 5a4.1 4.1 0 0 1 5.8 5.8l-1.8 1.8M12.8 17.2 11 19a4.1 4.1 0 0 1-5.8-5.8L7 11.4" />
    </>
  ),
  lock: (
    <>
      <rect x="4.6" y="10.4" width="14.8" height="10.6" rx="2.6" />
      <path d="M8.2 10.4V8a3.8 3.8 0 0 1 7.6 0v2.4" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5.6" width="18" height="12.8" rx="2.6" />
      <path d="m4.2 7.2 7.8 5.8 7.8-5.8" />
    </>
  ),
  printer: (
    <>
      <path d="M7 9.4V4h10v5.4" />
      <rect x="4" y="9.4" width="16" height="7" rx="2" />
      <path d="M7 14h10v6H7z" />
    </>
  ),
  "question-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.8 9.6a2.4 2.4 0 1 1 3.3 2.2c-.7.4-1.1.9-1.1 1.7M12 16.6v.4" />
    </>
  ),
  robot: (
    <>
      <rect x="4.2" y="8" width="15.6" height="11" rx="3.2" />
      <circle cx="9.2" cy="13" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="14.8" cy="13" r="1.2" fill="currentColor" stroke="none" />
      <path d="M12 8V4.8M9 19v1.6M15 19v1.6M4.2 12.4H2.6M20 12.4h1.6" />
    </>
  ),
  save: (
    <>
      <path d="M5 4h10.8L20 8.2V20H5z" />
      <path d="M9 4v5h6V4M8.6 14.4h6.8" />
    </>
  ),
  send: <path d="m4 12 16-8-6 16-2.5-5.5z" />,
  sync: (
    <>
      <path d="M4.6 12a7.4 7.4 0 0 1 12.6-5.2M19.4 12a7.4 7.4 0 0 1-12.6 5.2" />
      <path d="M17.4 3.4v3.6h-3.6M6.6 20.6V17h3.6" />
    </>
  ),
  thunderbolt: <path d="M13.6 3 6 13.6h4.9L9.4 21 18 10.4h-5z" />,
  undo: (
    <>
      <path d="M9 7.4 4.6 11.8 9 16.2" />
      <path d="M4.6 11.8h9.6a5 5 0 0 1 0 10h-4.4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.9" />
      <path d="M4.6 20.4a7.4 7.4 0 0 1 14.8 0" />
    </>
  ),
  wechat: (
    <>
      <path d="M9 4.4c4 0 7 2.3 7 5.1s-3 5.1-7 5.1c-1 0-1.9-.1-2.7-.4L3.6 15.8l1-2.6A4.9 4.9 0 0 1 2 9.5c0-2.8 3-5.1 7-5.1z" />
      <path d="M15.6 10.6c3 0 5.4 1.8 5.4 4 0 1.3-.7 2.4-1.8 3.2l.7 2-2.3-1.1c-.6.2-1.3.3-2 .3-2.4 0-4.4-1.1-5.1-2.7" />
    </>
  ),
  logout: (
    <>
      <path d="M14.5 7.5V5.4A1.9 1.9 0 0 0 12.6 3.5H6.4A1.9 1.9 0 0 0 4.5 5.4v13.2a1.9 1.9 0 0 0 1.9 1.9h6.2a1.9 1.9 0 0 0 1.9-1.9v-2.1" />
      <path d="M10 12h10M16.6 8.6 20 12l-3.4 3.4" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h11" />,
  compass: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="m14.8 9.2-1.6 4.4-4.4 1.6 1.6-4.4z" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof ICON_PATHS;
